import { useEffect, useState } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const STATUS_STYLES = {
  new: "text-accent border-accent/40",
  replied: "text-white border-white/40",
  archived: "text-fog border-white/20",
};

function InquiryCard({ adminKey, inquiry, onChanged }) {
  const [status, setStatus] = useState(inquiry.status || "new");
  const [notes, setNotes] = useState(inquiry.notes || "");
  const [saving, setSaving] = useState(false);
  const dirty = status !== (inquiry.status || "new") || notes !== (inquiry.notes || "");

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updateInquiry(adminKey, inquiry.id, { status, notes });
      onChanged();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete the inquiry from ${inquiry.name}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteInquiry(adminKey, inquiry.id);
      onChanged();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid={`inquiry-row-${inquiry.id}`} className="border border-white/10 p-6 transition-colors hover:bg-white/[0.02]">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <span className="font-display text-lg font-bold tracking-tight text-white">{inquiry.name}</span>
          {inquiry.company ? <span className="ml-2 text-sm text-fog">{inquiry.company}</span> : null}
          <span className={`ml-3 border px-2 py-0.5 font-mono text-[9px] tracking-[0.2em] ${STATUS_STYLES[status] || STATUS_STYLES.new}`}>
            {(inquiry.status || "new").toUpperCase()}
          </span>
        </div>
        <span className="font-mono text-[10px] text-fog">{fmt(inquiry.created_at)}</span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1">
        <a href={`mailto:${inquiry.email}`} className="font-mono text-xs text-accent hover:underline">{inquiry.email}</a>
        <span className="font-mono text-[10px] tracking-[0.2em] text-fog">{inquiry.visit_count || 0} VISITS ON RECORD</span>
        <span className="font-mono text-[10px] tracking-[0.2em] text-fog">
          EMAIL: {(inquiry.email_status || "—").toUpperCase()}
        </span>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-white/80">{inquiry.message}</p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select
          data-testid={`inquiry-status-select-${inquiry.id}`}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-white/20 bg-ink px-3 py-2 font-mono text-[11px] text-white outline-none focus:border-accent"
        >
          <option value="new">NEW</option>
          <option value="replied">REPLIED</option>
          <option value="archived">ARCHIVED</option>
        </select>
        <input
          data-testid={`inquiry-notes-input-${inquiry.id}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={4000}
          placeholder="Private note — visible only here"
          className="min-w-[220px] flex-1 border-b border-white/20 bg-transparent py-2 text-sm text-white placeholder-white/25 outline-none transition-colors focus:border-accent"
        />
        <button
          data-testid={`inquiry-save-button-${inquiry.id}`}
          onClick={save}
          disabled={!dirty || saving}
          className="flex items-center gap-2 bg-white px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-ink transition-colors hover:bg-accent disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          SAVE
        </button>
        <button
          data-testid={`inquiry-delete-button-${inquiry.id}`}
          onClick={del}
          className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-fog transition-colors hover:border-red-400 hover:text-red-400"
        >
          <Trash2 className="h-3.5 w-3.5" />
          DELETE
        </button>
      </div>
    </div>
  );
}

const FILTERS = [["", "ALL"], ["new", "NEW"], ["replied", "REPLIED"], ["archived", "ARCHIVED"]];

export default function Inquiries({ adminKey }) {
  const [inquiries, setInquiries] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");

  const load = (status = filter, query = q) => {
    adminApi
      .inquiries(adminKey, { status, q: query })
      .then((d) => setInquiries(d.inquiries))
      .catch((e) => setError(e.message));
  };

  useEffect(() => {
    load();
  }, [adminKey]);

  if (error) {
    return <p role="alert" data-testid="inquiries-error" className="mt-10 font-mono text-xs text-red-400">{error}</p>;
  }

  return (
    <div data-testid="admin-inquiries" className="mt-10">
      <div className="flex flex-wrap items-center gap-3">
        {FILTERS.map(([value, label]) => (
          <button
            key={label}
            data-testid={`inquiries-filter-${label.toLowerCase()}`}
            onClick={() => {
              setFilter(value);
              load(value, q);
            }}
            className={`border px-4 py-2 font-mono text-[10px] tracking-[0.2em] transition-colors ${
              filter === value ? "border-white bg-white text-ink" : "border-white/20 text-fog hover:border-white hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(filter, q);
          }}
          className="ml-auto flex items-center gap-2"
        >
          <input
            data-testid="inquiries-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, company, email…"
            className="w-56 border-b border-white/20 bg-transparent py-2 text-sm text-white placeholder-white/25 outline-none transition-colors focus:border-accent"
          />
          <button
            type="submit"
            data-testid="inquiries-search-button"
            className="border border-white/20 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors hover:border-white"
          >
            SEARCH
          </button>
        </form>
        <button
          data-testid="inquiries-export-button"
          onClick={() => adminApi.exportCsv(adminKey).catch((e) => toast.error(e.message))}
          className="border border-white/20 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors hover:border-accent hover:text-accent"
        >
          EXPORT CSV
        </button>
      </div>

      <div className="mt-8 flex flex-col gap-4">
        {inquiries === null ? (
          <p data-testid="inquiries-loading" className="font-mono text-xs text-fog">LOADING…</p>
        ) : inquiries.length === 0 ? (
          <p data-testid="inquiries-empty" className="font-mono text-xs text-fog">No inquiries match this view.</p>
        ) : (
          inquiries.map((inquiry) => (
            <InquiryCard key={inquiry.id} adminKey={adminKey} inquiry={inquiry} onChanged={() => load()} />
          ))
        )}
      </div>
    </div>
  );
}
